import React, { useEffect, useState } from 'react';
import { Paper, Typography, Box, useTheme, alpha, Skeleton, Tooltip as MuiTooltip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

const COLORS = [
  '#ef4444', '#f97316', '#f59e0b', '#eab308', '#84cc16',
  '#22c55e', '#10b981', '#14b8a6', '#06b6d4', '#3b82f6',
  '#6366f1', '#8b5cf6', '#a855f7', '#d946ef', '#ec4899'
];

const NotInterestedReasonsChart = ({ dateRangeState, selectedTeams = [], selectedAgents = [] }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    if (!dateRangeState?.currentStart || !dateRangeState?.currentEnd) {
      setData([]);
      setTotalCount(0);
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      try {
        const startDate = dateRangeState.currentStart.format('YYYY-MM-DD');
        const endDate = dateRangeState.currentEnd.format('YYYY-MM-DD');

        const requestData = {
          date_range: {
            start_date: startDate,
            end_date: endDate
          }
        };

        // Add team filter if provided
        if (selectedTeams && selectedTeams.length > 0) {
          requestData.team_filter = selectedTeams;
        }

        // Add agent filter if provided
        if (selectedAgents && selectedAgents.length > 0) {
          requestData.agent_filter = selectedAgents;
        }

        const response = await api.post('/report/dashboard/not-interested-reasons/', requestData);
        
        if (response.data) {
          setData(response.data.reasons || []);
          setTotalCount(response.data.total_count || 0);
        }
      } catch (error) {
        console.error('Error fetching not interested reasons:', error);
        setData([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [dateRangeState, selectedTeams, selectedAgents]);

  // Calculate font sizes based on counts (normalize to a range)
  const getFontSize = (count, maxCount, minCount) => {
    if (maxCount === minCount) return 24;
    const minSize = 14;
    const maxSize = 48;
    const ratio = (count - minCount) / (maxCount - minCount);
    return Math.round(minSize + (maxSize - minSize) * ratio);
  };

  // Prepare word cloud data with font sizes
  const prepareWordCloudData = () => {
    if (!data || data.length === 0) return [];
    
    const counts = data.map(item => item.count);
    const maxCount = Math.max(...counts);
    const minCount = Math.min(...counts);
    
    return data.map((item, index) => ({
      ...item,
      fontSize: getFontSize(item.count, maxCount, minCount),
      color: COLORS[index % COLORS.length]
    }));
  };

  if (loading) {
    return (
      <Paper sx={{ p: 3, borderRadius: 2 }}>
        <Skeleton variant="text" width="40%" height={32} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={350} sx={{ borderRadius: 1 }} />
      </Paper>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Paper sx={{ p: 3, borderRadius: 2, boxShadow: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: 'text.primary' }}>
          Not Interested Reasons
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: 350,
            color: 'text.secondary'
          }}
        >
          <Typography variant="body1">No data available for the selected period</Typography>
        </Box>
      </Paper>
    );
  }

  const wordCloudData = prepareWordCloudData();

  return (
    <Paper sx={{ p: 3, borderRadius: 2, boxShadow: 1 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, color: 'text.primary' }}>
          Not Interested Reasons
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Total: {totalCount} leads
        </Typography>
      </Box>
      
      <Box
        sx={{
          height: 350,
          width: '100%',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'center',
          alignContent: 'center',
          gap: { xs: 1.5, sm: 2, md: 2.5 },
          p: 3,
          overflow: 'auto',
          position: 'relative',
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.02)} 0%, ${alpha(theme.palette.secondary.main, 0.02)} 100%)`,
          borderRadius: 2,
          minHeight: 350
        }}
      >
        {wordCloudData.map((item, index) => {
          // Add some rotation variation for visual interest
          const rotation = (index % 3 - 1) * 5; // -5, 0, or 5 degrees
          
          const handleWordClick = () => {
            if (!dateRangeState?.currentStart || !dateRangeState?.currentEnd) return;
            
            const startDate = dateRangeState.currentStart.format('YYYY-MM-DD');
            const endDate = dateRangeState.currentEnd.format('YYYY-MM-DD');
            
            // Build query parameters
            const params = new URLSearchParams();
            params.set('stat_type', 'not-interested-reason');
            params.set('not_interested_reason_category', encodeURIComponent(item.reason));
            params.set('start_date', startDate);
            params.set('end_date', endDate);
            
            // Add team filter if provided
            if (selectedTeams && selectedTeams.length > 0) {
              params.set('teams', selectedTeams.join(','));
            }
            
            // Add agent filter if provided
            if (selectedAgents && selectedAgents.length > 0) {
              params.set('agents', selectedAgents.join(','));
            }
            
            // Navigate to leads page
            navigate(`/leads?${params.toString()}`, {
              state: {
                from: 'dashboard',
                dashboardStartDate: startDate,
                dashboardEndDate: endDate,
                selectedTeams: selectedTeams,
                selectedAgents: selectedAgents
              }
            });
          };
          
          return (
            <MuiTooltip
              key={index}
              title={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {item.reason}
                  </Typography>
                  <Typography variant="body2">
                    Count: {item.count}
                  </Typography>
                  <Typography variant="body2">
                    Percentage: {item.percentage}%
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic', fontSize: '0.75rem' }}>
                    Click to view leads
                  </Typography>
                </Box>
              }
              arrow
            >
              <Box
                component="span"
                onClick={handleWordClick}
                sx={{
                  fontSize: `${item.fontSize}px`,
                  fontWeight: item.count > totalCount * 0.2 ? 700 : item.count > totalCount * 0.1 ? 600 : 500,
                  color: item.color,
                  cursor: 'pointer',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  display: 'inline-block',
                  px: 1.5,
                  py: 0.75,
                  borderRadius: 1.5,
                  transform: `rotate(${rotation}deg)`,
                  lineHeight: 1.2,
                  '&:hover': {
                    transform: `rotate(0deg) scale(1.15)`,
                    backgroundColor: alpha(item.color, 0.15),
                    boxShadow: `0 4px 12px ${alpha(item.color, 0.4)}`,
                    zIndex: 10,
                    position: 'relative'
                  }
                }}
              >
                {item.reason}
              </Box>
            </MuiTooltip>
          );
        })}
      </Box>
    </Paper>
  );
};

export default React.memo(NotInterestedReasonsChart);
